import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { RequestListFilterComponent } from 'src/app/features/my-requests/request-list/request-list-filter/request-list-filter.component';
import { RequestAPIService } from 'src/app/extensions/services/api/request-api.service';
import { UserAPIService } from 'src/app/extensions/services/api/user-api.service';
import { isEqual } from 'lodash';
import { scrollTop } from 'src/app/extensions/utilities';
import { ScrollService } from 'src/app/extensions/services/scroll.service';
import { Subscription } from 'rxjs';
import { ASSET_DOCUMENT_WRITE_RIGHTS } from 'src/app/extensions/constants/right-constants';
import { forkJoin } from 'rxjs';
import { PartnerAPIService } from 'src/app/extensions/services/api/partner-api.service';
import { DictionaryAPIService } from 'src/app/extensions/services/api/dictionary-api.service';
import {
  DictionaryContractType,
  DictionaryRequestStatus,
} from 'src/app/extensions/constants/dictionaryConstants/dictionaryConstants';
import { ContractAPIService } from 'src/app/extensions/services/api/contracts-api.service';
import { ENV_CONFIG } from 'src/app/core/injection.token';

@Component({
  selector: 'app-request-list',
  templateUrl: './request-list.component.html',
  styleUrls: ['./request-list.component.scss'],
  providers: [ScrollService],
  standalone: false,
})
export class RequestListComponent implements OnInit, OnDestroy {
  requests: app.models.Requests[] = [];
  requestsProps: app.models.DynamicPropsDictionary;
  public hasData = true;
  public hasFilterData = true;

  private pageIndex = 1;
  private maxIndex: number;
  public pageFilter: app.models.GetRequestsModelPageFilter;
  public initialFilterState: app.models.GetRequestsModelPageFilter;
  public activeFilterCount = 0;

  userRights: string[];
  readOnly: boolean = true;

  public subscriptions: Subscription[] = [];
  public showBackToTop: boolean = false;
  warningMessages: string[] = [];
  public modalRequestsStatuses: app.models.DictionaryDataModel[] = [];
  filteredActiveContracts: app.models.ActiveContractModel[] = [];
  noActiveContracts: boolean = false;

  bypassRestrictionsAssetDocumentRequest: boolean = false;
  public isFiltered = false;

  constructor(
    private _requestAPIService: RequestAPIService,
    private _router: Router,
    private _userAPIService: UserAPIService,
    private _ngbModal: NgbModal,
    private _scrollService: ScrollService,
    private _partnerAPIService: PartnerAPIService,
    private _dictionaryAPIService: DictionaryAPIService,
    private _contractAPIService: ContractAPIService,
    @Inject(ENV_CONFIG) private _envConfig: env.EnvConfigurationData
  ) {}

  ngOnInit(): void {
    this.bypassRestrictionsAssetDocumentRequest = JSON.parse(
      this._envConfig.BYPASS_RESTRICTIONS_ASSET_DOCUMENT_REQUEST || 'false'
    );

    this.pageFilter = {
      statusesIds: [],
    } as app.models.GetRequestsModelPageFilter;
    this.initialFilterState = structuredClone(this.pageFilter);

    this._dictionaryAPIService.getDictionaryRequestStatuses().subscribe(requestStatuses => {
      this.modalRequestsStatuses = requestStatuses.data;
      this.pageFilter.statusesIds = this.modalRequestsStatuses
        .filter(status => status.code !== DictionaryRequestStatus.CLOSED)
        .map(status => status.id);

      this.initialFilterState = structuredClone(this.pageFilter);
      this.activeFilterCount = this.pageFilter.statusesIds.length;
      this.loadData();
    });

    let backToTopSubscription = this._scrollService.obsShowBackToTop.subscribe(x => {
      this.showBackToTop = x;
    });
    this.subscriptions.push(backToTopSubscription);

    this._scrollService.obsFetchData.subscribe(() => {
      if (this.pageIndex <= this.maxIndex) {
        this.pageIndex++;
        this._requestAPIService.getRequests(this.pageIndex, this.pageFilter).subscribe(result => {
          this.requests = [...this.requests, ...result.data.values];
          this.maxIndex = result.data.totalPages;
          if (this.pageIndex <= this.maxIndex) {
            this._scrollService.waitingForDataFetch = false;
          }
        });
      }
    });

    this._contractAPIService.getActiveContracts().subscribe(contracts => {
      this.filteredActiveContracts = contracts.filter(
        contract => contract.dictionaryContractType.code !== DictionaryContractType.CREDIT
      );
      this.noActiveContracts = this.filteredActiveContracts.length === 0;
    });
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach(subscription => {
        subscription.unsubscribe();
      });
    }
  }

  loadData(filterMode: boolean = false) {
    this.warningMessages = [];

    forkJoin([
      this._userAPIService.getUserRights(),
      this._requestAPIService.getRequests(this.pageIndex, this.pageFilter),
      this._partnerAPIService.getPartnerRequestCreationRestrictions(),
    ]).subscribe(([userRightsResponse, requestsResponse, requestCreationRestrictions]) => {
      this.userRights = userRightsResponse.rights.map(x => x.code);
      this.readOnly = !this.userRights.some(userRight => ASSET_DOCUMENT_WRITE_RIGHTS.includes(userRight));

      if (requestCreationRestrictions.isRestricted) {
        this.warningMessages.push('requests.cards.contactSupportWarning');
      }
      if (requestCreationRestrictions.hasUnpaidInvoices) {
        this.warningMessages.push('requests.cards.unpaidInvoicesWarning');
      }

      this.requests = requestsResponse.data.values;
      this.requestsProps = requestsResponse.props;
      this.maxIndex = requestsResponse.data.totalPages;
      this.isFiltered = filterMode;
      if (filterMode) {
        this.hasFilterData = this.requests.length > 0;
      } else {
        this.hasData = this.requests.length > 0;
      }

      this._scrollService.waitingForDataFetch = this.pageIndex > this.maxIndex;
    });
  }

  public filterHandler() {
    let modal = this._ngbModal.open(RequestListFilterComponent, { backdrop: 'static' });
    modal.componentInstance.modalFilters = this.pageFilter;
    modal.componentInstance.initialFilterState = this.initialFilterState;
    modal.componentInstance.modalRequestsStatuses = this.modalRequestsStatuses;

    modal.closed.subscribe(result => {
      if (result !== false) {
        this.initialFilterState = {
          ...this.initialFilterState,
          statusesIds: this.pageFilter.statusesIds,
        };
        this.activeFilterCount = result;
        this.pageIndex = 1;
        if (!isEqual(this.pageFilter, this.initialFilterState)) {
          this.pageFilter = structuredClone(this.initialFilterState);
        }

        this.loadData(true);
      }
    });
  }

  public resetFiltersHandler() {
    this.activeFilterCount = 0;

    this.initialFilterState = {
      ...this.initialFilterState,
      statusesIds: this.pageFilter.statusesIds,
    };

    if (!isEqual(this.pageFilter, this.initialFilterState)) {
      this.pageFilter = structuredClone(this.initialFilterState);
    }

    this.pageFilter = {
      statusesIds: [],
    } as app.models.GetRequestsModelPageFilter;

    this.pageIndex = 1;
    this.hasFilterData = true;
    this.loadData();
  }

  redirectNewRequest() {
    this._router.navigate(['requests/new']);
  }

  downloadPolicy(id: string) {
    return;
  }

  viewDetailsHandler = (requestId: string) => {
    this._router.navigate(['requests', requestId]);
  };

  redirectRequestTypeHandler() {
    this._router.navigate(['requests/entry']);
  }

  scrollToTop(): void {
    scrollTop();
  }
}
